import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { setExportOptionsHandler } from "@/lib/pdf";
import { FilePdf, FileText } from "@phosphor-icons/react";

export const EXPORT_ROLES = [
  "Sport Director",
  "Match Analyst",
  "Head of Scouting",
  "Team Doctor",
  "Head Coach",
  "Assistant Coach",
  "Goalkeeper Coach",
  "Fitness Coach",
  "Fisioterapista",
  "Scout",
  "Head of recruitment",
  "Head of women",
  "Manager",
  "Team manager",
  "Secretary",
  "RTP",
];

// Imperative bridge: pdf.js askExportOptions() calls the handler registered below.
export function ExportOptionsHost() {
  const [resolver, setResolver] = useState(null);
  const [cover, setCover] = useState(true);
  const [author, setAuthor] = useState("");
  const [role, setRole] = useState("");
  const [compareList, setCompareList] = useState([]);   // [{id,name,role}] roster/scouting candidates
  const [compareOn, setCompareOn] = useState(false);
  const [compareId, setCompareId] = useState("");

  useEffect(() => {
    setExportOptionsHandler((opts) =>
      new Promise((resolve) => {
        try {
          setAuthor(localStorage.getItem("pc_export_author") || "");
          setRole(localStorage.getItem("pc_export_role") || "");
        } catch (e) { /* ignore */ }
        setCover(true);
        setCompareList(Array.isArray(opts?.compare) ? opts.compare : []);
        setCompareOn(false);
        setCompareId("");
        setResolver(() => resolve);
      })
    );
    return () => setExportOptionsHandler(null);
  }, []);

  const done = (result) => {
    const r = resolver;
    setResolver(null);
    if (result) {
      try {
        if (result.author) localStorage.setItem("pc_export_author", result.author);
        if (result.role) localStorage.setItem("pc_export_role", result.role);
      } catch (e) { /* ignore */ }
    }
    if (r) r(result);
  };

  return (
    <Dialog open={!!resolver} onOpenChange={(o) => { if (!o) done(null); }}>
      <DialogContent className="sm:max-w-[440px]" data-testid="export-options-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Opzioni di esportazione PDF</DialogTitle>
          <DialogDescription>Scegli il formato e indica l'autore del dossier.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Formato</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="export-format-dossier"
                onClick={() => setCover(true)}
                className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition-colors ${cover ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-white/5"}`}
              >
                <FilePdf size={22} weight={cover ? "fill" : "regular"} />
                Dossier con copertina
              </button>
              <button
                type="button"
                data-testid="export-format-simple"
                onClick={() => setCover(false)}
                className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition-colors ${!cover ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-white/5"}`}
              >
                <FileText size={22} weight={!cover ? "fill" : "regular"} />
                PDF semplice
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="export-author" className="text-xs uppercase tracking-wide text-muted-foreground">Autore del dossier</Label>
            <Input
              id="export-author"
              data-testid="export-author-input"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Nome e cognome"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Ruolo dell'autore</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="export-role-select"><SelectValue placeholder="Seleziona ruolo" /></SelectTrigger>
              <SelectContent>
                {EXPORT_ROLES.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`export-role-${r.replace(/\s+/g, "-").toLowerCase()}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {compareList.length > 0 && (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Confronta con calciatrice rosa</Label>
                <div className="flex gap-1">
                  <button type="button" data-testid="export-compare-no" onClick={() => { setCompareOn(false); setCompareId(""); }}
                    className={`rounded-sm px-2.5 py-1 text-xs transition-colors ${!compareOn ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-white/5"}`}>No</button>
                  <button type="button" data-testid="export-compare-yes" onClick={() => setCompareOn(true)}
                    className={`rounded-sm px-2.5 py-1 text-xs transition-colors ${compareOn ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:bg-white/5"}`}>Sì</button>
                </div>
              </div>
              {compareOn && (
                <Select value={compareId} onValueChange={setCompareId}>
                  <SelectTrigger data-testid="export-compare-select"><SelectValue placeholder="Scegli la calciatrice" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {compareList.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`export-compare-${c.id}`}>{c.name}{c.role ? ` · ${c.role}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => done(null)} data-testid="export-cancel">Annulla</Button>
          <Button
            onClick={() => done({ cover, author: author.trim(), role, compareId: compareOn ? compareId : "" })}
            data-testid="export-confirm"
            className="gap-2 font-display uppercase tracking-wide"
          >
            <FilePdf size={16} weight="fill" /> Esporta PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
